import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FolderKanban, Clock, Calendar, AlertTriangle, FileWarning,
  Upload as UploadIcon, TrendingUp, Users as UsersIcon,
} from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const Kpi = ({ label, value, sub, icon: Icon, accent = "white" }) => (
  <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-3 sm:p-5 relative overflow-hidden">
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <div className="label-eyebrow truncate">{label}</div>
        <div className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold mt-2 tabular" style={{ color: accent }}>{value}</div>
        {sub && <div className="text-[10px] sm:text-xs text-[#777] font-mono mt-1">{sub}</div>}
      </div>
      <Icon className="h-5 w-5 text-[#555] shrink-0" />
    </div>
  </Card>
);

export default function AdminDashboard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard/admin").then((r) => setD(r.data));
  }, []);

  if (!d) {
    return <div className="font-mono text-[#666] label-eyebrow">LOADING CONTROL ROOM…</div>;
  }

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 p-4 sm:p-6 bg-brand-soft border border-[#1c1c1c] -mx-3 sm:-mx-4 lg:-mx-8 -mt-3 sm:-mt-4 lg:-mt-8 mb-2">
        <div>
          <div className="label-eyebrow" style={{ color: "#8a83d4" }}>CONTROL ROOM</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold uppercase tracking-tight mt-1">Overview</h1>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono text-[#b9b2e8]">
          <span className="h-2 w-2 bg-emerald-500 rounded-full pulse-dot" />
          LIVE • {new Date().toLocaleString()}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="kpi-grid">
        <Kpi label="Active Projects" value={d.active_projects} icon={FolderKanban} />
        <Kpi label="Clocked-In Now" value={d.clocked_in_now} icon={UsersIcon} accent="#10B981" sub="live" />
        <Kpi label="Hours Today" value={d.hours_today} icon={Clock} />
        <Kpi label="Hours This Week" value={d.hours_week} icon={TrendingUp} />
        <Kpi label="Hours This Month" value={d.hours_month} icon={Calendar} />
      </div>

      {/* Charts + tables */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="label-eyebrow">MANPOWER DAYS PER PROJECT</div>
            <span className="font-mono text-xs text-[#777]">{d.project_stats.length} active</span>
          </div>
          {d.project_stats.length === 0 ? (
            <div className="text-[#666] font-mono text-sm py-10 text-center">NO DATA YET</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={d.project_stats} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid stroke="#1a1a1a" vertical={false} />
                <XAxis dataKey="project_name" stroke="#555" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#555" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "#0a0a0a", border: "1px solid #222", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11 }}
                  cursor={{ fill: "rgba(255,255,255,0.04)" }}
                />
                <Bar dataKey="manpower_days" fill="#3b2bd0" name="Manpower Days" />
                <Bar dataKey="total_hours" fill="#6e5fe8" name="Total Hours" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-5">
          <div className="label-eyebrow mb-4">QUICK STATUS</div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center justify-between border-b border-[#1c1c1c] pb-3">
              <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Pending Leaves</span>
              <Link to="/leaves" className="font-mono tabular text-amber-400">{d.pending_leaves}</Link>
            </li>
            <li className="flex items-center justify-between border-b border-[#1c1c1c] pb-3">
              <span className="flex items-center gap-2"><FileWarning className="h-4 w-4 text-amber-500" /> Missing Reports Today</span>
              <span className="font-mono tabular text-amber-400">{d.missing_daily_reports.length}</span>
            </li>
            <li className="flex items-center justify-between border-b border-[#1c1c1c] pb-3">
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-red-500" /> Incomplete Clock-outs</span>
              <span className="font-mono tabular text-red-400">{d.incomplete_clockouts.length}</span>
            </li>
            <li className="flex items-center justify-between">
              <span className="flex items-center gap-2"><UploadIcon className="h-4 w-4 text-[#888]" /> Recent Uploads</span>
              <Link to="/uploads" className="font-mono tabular">{d.recent_uploads.length}</Link>
            </li>
          </ul>
        </Card>
      </div>

      {/* Project stats table */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c]">
        <div className="p-5 border-b border-[#1c1c1c] flex items-center justify-between">
          <div className="label-eyebrow">PROJECT BREAKDOWN</div>
          <Link to="/projects" className="text-xs font-mono text-[#999] hover:text-white">VIEW ALL →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="project-stats-table">
            <thead>
              <tr className="text-left text-[#666] label-eyebrow">
                <th className="px-5 py-3 font-normal">PROJECT</th>
                <th className="px-5 py-3 font-normal text-right">MANPOWER DAYS</th>
                <th className="px-5 py-3 font-normal text-right">TOTAL HOURS</th>
              </tr>
            </thead>
            <tbody>
              {d.project_stats.map((p) => (
                <tr key={p.project_id} className="border-t border-[#1c1c1c] hover:bg-[#101010]">
                  <td className="px-5 py-3 font-medium">{p.project_name}</td>
                  <td className="px-5 py-3 text-right font-mono tabular">{p.manpower_days}</td>
                  <td className="px-5 py-3 text-right font-mono tabular">{p.total_hours}</td>
                </tr>
              ))}
              {d.project_stats.length === 0 && (
                <tr><td colSpan={3} className="px-5 py-10 text-center text-[#666] font-mono">NO DATA YET</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Recent uploads */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c]">
        <div className="p-5 border-b border-[#1c1c1c] label-eyebrow">RECENT UPLOADS</div>
        <ul className="divide-y divide-[#1c1c1c]">
          {d.recent_uploads.length === 0 && <li className="p-5 text-center font-mono text-[#666] text-sm">NO UPLOADS YET</li>}
          {d.recent_uploads.map((u) => (
            <li key={u.id} className="p-4 flex items-center justify-between text-sm">
              <div className="min-w-0">
                <div className="font-medium truncate">{u.original_filename}</div>
                <div className="font-mono text-xs text-[#666] mt-0.5">
                  {u.uploaded_by_name} • {new Date(u.uploaded_at).toLocaleString()}
                </div>
              </div>
              {u.category && <Badge variant="outline" className="rounded-none border-[#333] font-mono text-xs">{u.category}</Badge>}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
